// Date helpers for <DateDropdown>. ISO YYYY-MM-DD on the wire, DD/MM/YYYY for
// UK display. Pure functions; no moment dep (the primitive must stay light).
//
// All functions operate on local-time Date objects. The SPA's filter use-case
// is "the day the operator clicks", not a timezone-aware instant — UTC parsing
// would cause a one-day drift for users west of UTC late at night. Backend
// filter endpoints accept the bare YYYY-MM-DD and resolve in their own zone.

export type IsoDate = string; // YYYY-MM-DD

export function isValidIso(s: string | null | undefined): s is IsoDate {
  if (!s) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  // Reject calendar-impossible dates like 2026-13-01 / 2026-02-30. The Date
  // constructor silently rolls those over (Feb 30 → Mar 2), so we round-trip
  // back to ISO and require the input to match.
  const [y, m, d] = s.split('-').map((p) => parseInt(p, 10));
  const probe = new Date(y, m - 1, d);
  return (
    probe.getFullYear() === y &&
    probe.getMonth() === m - 1 &&
    probe.getDate() === d
  );
}

export function parseIsoToLocal(s: IsoDate): Date {
  // Construct a local-time Date from YYYY-MM-DD without invoking the Date
  // constructor's UTC interpretation (which `new Date('2026-05-06')` does).
  const [y, m, d] = s.split('-').map((p) => parseInt(p, 10));
  return new Date(y, m - 1, d);
}

export function formatIso(date: Date): IsoDate {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatUk(iso: IsoDate | null | undefined): string {
  if (!iso || !isValidIso(iso)) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function addMonths(date: Date, n: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}

export function addDays(date: Date, n: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isBeforeDay(a: Date, b: Date): boolean {
  return formatIso(a) < formatIso(b);
}

export function isAfterDay(a: Date, b: Date): boolean {
  return formatIso(a) > formatIso(b);
}

export function isWithinRange(
  day: Date,
  start: Date | null,
  end: Date | null,
): boolean {
  if (!start || !end) return false;
  const iso = formatIso(day);
  return iso >= formatIso(start) && iso <= formatIso(end);
}

// Build a 6×7 month grid (42 cells). Cells outside the active month carry
// `outside: true` so the renderer can dim them while keeping the grid square.
export interface MonthCell {
  date: Date;
  iso: IsoDate;
  day: number;
  outside: boolean;
}

export function buildMonthGrid(viewMonth: Date, weekStartsOnMonday = true): MonthCell[] {
  const first = startOfMonth(viewMonth);
  const startWeekday = first.getDay(); // 0=Sun..6=Sat
  // Shift so Monday=0 when weekStartsOnMonday (UK convention).
  const offset = weekStartsOnMonday ? (startWeekday + 6) % 7 : startWeekday;
  const gridStart = addDays(first, -offset);
  const cells: MonthCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = addDays(gridStart, i);
    cells.push({
      date: d,
      iso: formatIso(d),
      day: d.getDate(),
      outside: d.getMonth() !== viewMonth.getMonth(),
    });
  }
  return cells;
}

export const UK_WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const UK_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function monthLabel(date: Date): string {
  return `${UK_MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

// Today, normalised to local midnight for comparisons.
export function today(): Date {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}
